import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  AddCircleOutline,
  Delete,
  Edit,
  Visibility,
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
  Button,
  IconButton,
  Tooltip,
  Switch,
  TextField,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import ProjectViewController from "../../redux/actions/ProjectApiAction";
import ProjectFormModal from "./AddProjectForm";
import ProjectViewModal from "./ProjectView";
import ProjectMobileView from "./ProjectMobileView";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import ExportButton from "../../components/ag grid/ExportButton";
import ListPageLayout from "../../components/layout/ListPageLayout";
import ConfirmDialog from "../../components/ConfirmDialog";
import { toolbarButtonSx, inputSx } from "../../components/auth/authTheme";
import usePermission from "../../utils/usePermission";

import "../../styles/styles.css";
import "../../styles/layout.css";

const EXPORT_COLUMNS = ["title", "start_date", "end_date", "is_active"];

const ProjectList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { hasPermission } = usePermission();

  const { getProjects, deleteProject, updateProject } = ProjectViewController();
  const { projects } = useSelector((state) => state.project);

  const [showModal, setShowModal] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, projectId: null });
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const canCreate = hasPermission("project_list", "create");
  const canEdit = hasPermission("project_list", "edit");
  const canDelete = hasPermission("project_list", "delete");
  const canExport = hasPermission("project_list", "export");

  useEffect(() => {
    dispatch(getProjects());
  }, [dispatch]);

  const filteredProjects = useMemo(() => {
    if (!searchTerm.trim()) return projects || [];
    const query = searchTerm.toLowerCase();
    return (projects || []).filter(
      (p) =>
        p.title?.toLowerCase().includes(query) ||
        p.scope?.toLowerCase().includes(query)
    );
  }, [projects, searchTerm]);

  const handleDelete = useCallback((projectId) => {
    setDeleteDialog({ open: true, projectId });
  }, []);

  const confirmDelete = useCallback(() => {
    if (deleteDialog.projectId) {
      dispatch(deleteProject(deleteDialog.projectId));
      setDeleteDialog({ open: false, projectId: null });
    }
  }, [deleteDialog.projectId, dispatch, deleteProject]);

  const handleEdit = useCallback((project) => {
    setEditProject(project);
    setShowModal(true);
  }, []);

  const handleView = useCallback((project) => {
    setSelectedProject(project);
    setShowViewModal(true);
  }, []);

  const handleStatusToggle = useCallback(
    (params) => {
      const updatedValue = !params.data.is_active;
      params.node.setDataValue("is_active", updatedValue);
      dispatch(updateProject(params.data.id, { ...params.data, is_active: updatedValue }));
    },
    [dispatch, updateProject]
  );

  const handleMobileStatusToggle = useCallback(
    (project) => {
      const updatedValue = !project.is_active;
      dispatch(updateProject(project.id, { ...project, is_active: updatedValue }));
    },
    [dispatch, updateProject]
  );

  const openAddModal = useCallback(() => {
    setEditProject(null);
    setShowModal(true);
  }, []);

  const handleToggleSelect = useCallback((project) => {
    setSelectedRows((prev) => {
      const exists = prev.some((row) => row.id === project.id);
      if (exists) return prev.filter((row) => row.id !== project.id);
      return [...prev, project];
    });
  }, []);

  const handleMobileExport = useCallback(() => {
    if (selectedRows.length === 0) return;
    const filteredRows = selectedRows.map((row) => {
      const filtered = {};
      EXPORT_COLUMNS.forEach((key) => {
        filtered[key] = row[key];
      });
      return filtered;
    });
    const worksheet = XLSX.utils.json_to_sheet(filteredRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, "projects.xlsx");
  }, [selectedRows]);

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
        headerClass: "checkbox-selection-header",
        cellClass: "checkbox-selection-cell",
      },
      {
        headerName: "SR No.",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      {
        headerName: "Title",
        field: "title",
        flex: 1,
        minWidth: 180,
        cellClass: "ag-cell-ellipsis",
      },
      {
        headerName: "Start Date",
        field: "start_date",
        width: 120,
        maxWidth: 130,
        minWidth: 120,
        suppressSizeToFit: true,
      },
      {
        headerName: "End Date",
        field: "end_date",
        width: 120,
        maxWidth: 130,
        minWidth: 120,
        suppressSizeToFit: true,
      },
      {
        headerName: "Status",
        field: "is_active",
        width: 90,
        maxWidth: 100,
        minWidth: 90,
        suppressSizeToFit: true,
        cellRenderer: (params) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Switch
              checked={params.value}
              onChange={() => handleStatusToggle(params)}
              color="primary"
              size="small"
            />
          </div>
        ),
      },
      {
        headerName: "Actions",
        pinned: "right",
        width: 120,
        minWidth: 120,
        maxWidth: 120,
        suppressSizeToFit: true,
        sortable: false,
        cellRenderer: (params) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            <Tooltip title="View">
              <IconButton size="small" onClick={() => handleView(params.data)}>
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
            {canEdit && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => handleEdit(params.data)}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  sx={{ color: "#d32f2f" }}
                  onClick={() => handleDelete(params.data.id)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [canEdit, canDelete, handleView, handleEdit, handleDelete, handleStatusToggle]
  );

  return (
    <>
      {isMobile ? (
        <ProjectMobileView
          projects={filteredProjects}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedRows={selectedRows}
          onToggleSelect={handleToggleSelect}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onStatusToggle={handleMobileStatusToggle}
          onAdd={openAddModal}
          onExport={handleMobileExport}
          canCreate={canCreate}
          canEdit={canEdit}
          canDelete={canDelete}
          canExport={canExport}
        />
      ) : (
      <ListPageLayout
        title="Project List"
        subtitle="Manage projects, timelines, and team assignments"
        headerAction={
          canCreate ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCircleOutline />}
              onClick={openAddModal}
              sx={toolbarButtonSx}
            >
              Add Project
            </Button>
          ) : null
        }
        toolbarLeft={
          <TextField
            size="small"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ ...inputSx, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        }
        toolbarRight={
          <>
            {canExport && (
              <ExportButton
                gridRef={gridRef}
                fileName="projects.xlsx"
                exportColumns={["title", "start_date", "end_date", "is_active"]}
                disabled={selectedRows.length === 0}
              />
            )}
            <PageSizeSelector
              pageSize={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            />
          </>
        }
      >
        <div className="list-page-grid">
          <ReusableGrid
            rowData={filteredProjects}
            columnDefs={columnDefs}
            gridRef={gridRef}
            pagination={true}
            pageSize={pageSize}
            getRowId={(params) => String(params.data.id)}
            onSelectionChanged={(rows) => setSelectedRows(rows)}
            emptyMessage="No projects yet"
            emptyHint={
              canCreate
                ? 'Click "Add Project" to create your first project.'
                : "No project records are available."
            }
          />
        </div>
      </ListPageLayout>
      )}

      {showModal && (
        <ProjectFormModal
          show={showModal}
          handleClose={() => setShowModal(false)}
          initialData={editProject}
        />
      )}

      <ProjectViewModal
        show={showViewModal}
        handleClose={() => setShowViewModal(false)}
        selectedProject={selectedProject}
      />

      <ConfirmDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, projectId: null })}
        onConfirm={confirmDelete}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone."
      />
    </>
  );
};

export default ProjectList;
